import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';

import { Title } from '@angular/platform-browser';
import { Router, ActivatedRoute, NavigationEnd } from '@angular/router';

import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';
import { EnumErrorStates, ErrorObject } from '@inmoment/the-kitchen';

import { HasTitle } from 'app/has-title';
import { FullStoryService } from 'app/modules/full-story/services/full-story.service';
import { AnalyticsService } from 'app/providers/analytics.service';
import { SemantriaAccount } from 'app/providers/bridge-api/account.service';
import { AuthService, Session } from 'app/providers/bridge-api/auth.service';
import { SemantriaPreferences } from 'app/providers/bridge-api/preferences.service';
import {
  SocialLoginService,
  SocialChoice,
  setNickname,
  getNicknameFromOrigin,
  getNicknameFromParams,
} from 'app/providers/bridge-api/social-login.service';
import { RealtimeService } from 'app/providers/realtime.service';
import { GroupService } from 'app/providers/semantria-api/group.service';

import { AlertService } from 'app/services/alert.service';

import {
  injectComponentTelemetryService,
  EnableComponentTelemetry,
  EnableClassTelemetry,
} from 'app/services/otel.service';
import { LocalStorageService } from '../../../../services/local-storage.service';

@EnableClassTelemetry()
@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  animations: [
    trigger('flyInRight', [
      state('in', style({ transform: 'translateX(0)' })),
      transition('* => in', [
        style({
          transform: 'translateX(100%)',
        }),
        animate('750ms ease-out'),
      ]),
    ]),
  ],
  standalone: false,
})
export class LoginComponent extends HasTitle implements OnInit, OnDestroy {
  @EnableComponentTelemetry() otelService = injectComponentTelemetryService();
  public appVersion = (window as any).appVersion;

  static readonly INMOMENT_SERVICE_NAME = 'inmoment';
  static readonly AUTO_LOGIN_NICKNAMES = ['inmoment', 'nike', 'alightinmoment'];
  static readonly USERNAME_PASSWORD_SOCIAL_CHOICE = 'usernamepassword';

  public isLoading = false;
  public error = '';
  public login = '';
  public password = '';
  public modifier = '';
  public envName = '';
  public customMessage = '';
  public customIcon = 'campaign';
  public nickname: string;
  public showUserLogin: boolean;
  public socialChoices: SocialChoice[];

  public loginNameStatus: ErrorObject | null;
  public passwordStatus: ErrorObject | null;
  public currentYear: number;
  public loadLoginPage = false;
  public flyInState: string;
  private initialLoad = true;
  private routingSub$: Subscription;
  private localStorage = inject(LocalStorageService);

  get isSignInDisabled() {
    return (
      this.isLoading ||
      this.login.length < 1 ||
      this.login.trim() === '' ||
      this.password.length < 1 ||
      this.password.trim() === ''
    );
  }

  constructor(
    protected titleService: Title,
    private router: Router,
    private route: ActivatedRoute,
    private authService: AuthService,
    private alertService: AlertService,
    private groupService: GroupService,
    private preferencesService: SemantriaPreferences,
    private fullStoryService: FullStoryService,
    private googleAnalyticsService: AnalyticsService,
    private socialLoginService: SocialLoginService,
    private semantriaAccount: SemantriaAccount,
    private realtimeService: RealtimeService,
    private translocoService: TranslocoService
  ) {
    super(titleService);
  }

  async ngOnInit() {
    this.flyInState = 'in';
    this.currentYear = new Date().getFullYear();
    const urlError: string = this.route.snapshot.queryParamMap.get('error');
    if (urlError) {
      this.error = urlError;
    }
    const groupId: string = this.route.snapshot.queryParamMap.get('group');
    const accountId: string = this.route.snapshot.queryParamMap.get('accountId');
    const authProviderToken: string = this.route.snapshot.queryParamMap.get('token');
    const redirectTo: string = this.route.snapshot.queryParamMap.get('redirectTo');
    const language: string = this.route.snapshot.queryParamMap.get('lang');
    const stashToken: string = this.route.snapshot.queryParamMap.get('stashedSession');
    const aliasType: string = this.route.snapshot.queryParamMap.get('aliasType');
    const aliasName: string = this.route.snapshot.queryParamMap.get('dashboardAlias');

    if (language) {
      this.translocoService.setActiveLang(language);
    }

    this.nickname = this.socialLoginService.nickname;
    // Only persist nickname to localStorage when explicitly provided via URL.
    // Don't re-save from localStorage — logout already clears it (auth.service.ts).
    const urlNickname =
      getNicknameFromOrigin(window.location.origin) ||
      getNicknameFromParams(window.location.search);
    if (urlNickname) {
      setNickname.bind(this)(urlNickname);
    }

    this.isLoading = true;
    let pendingLoginSession: Session | null = null;
    try {
      pendingLoginSession = await this.authService.checkPendingLogin();
    } catch (err) {
      this.isLoading = false;
      this.error = 'SessionExpired';
      this.loadLoginPage = true;
      return;
    }

    if (pendingLoginSession !== null) {
      this.realtimeService.setToken(pendingLoginSession.rt_token);
      this.navigateOnLoggedIn(groupId);
      return;
    }

    const dashToken = this.route.snapshot.queryParamMap.get('dashToken');
    if (dashToken) {
      try {
        const session = await this.authService.redeemDashToken(dashToken);
        this.realtimeService.setToken(session.rt_token);
        this.navigateOnLoggedIn(groupId);
        return;
      } catch {
        this.isLoading = false;
        this.error = 'ShareLinkExpired';
        this.loadLoginPage = true;
        return;
      }
    }

    this.isLoading = false;

    const loggedInToDifferentAccount = accountId !== this.authService.accountId;
    const socialAutoLoginEnabled =
      !this.error && (!this.authService.isLoggedIn() || loggedInToDifferentAccount);
    if (
      LoginComponent.AUTO_LOGIN_NICKNAMES.find((nickname) => nickname === this.nickname) &&
      socialAutoLoginEnabled
    ) {
      return this.submitSocial(this.nickname);
    }
    const socialChoices = await this.socialLoginService.getChoices();
    const usernamePasswordChoiceIdx = socialChoices.findIndex(
      (social) => social.key === LoginComponent.USERNAME_PASSWORD_SOCIAL_CHOICE
    );
    if (socialChoices.length === 1 && socialAutoLoginEnabled && usernamePasswordChoiceIdx === -1) {
      return this.submitSocial(socialChoices[0].key);
    }
    if (usernamePasswordChoiceIdx !== -1) {
      socialChoices.splice(usernamePasswordChoiceIdx, 1);
    }
    this.socialChoices = socialChoices;
    this.showUserLogin = this.socialChoices.length === 0 || usernamePasswordChoiceIdx !== -1;
    this.setTitle('Login');
    const appVolatile = (window as any).appVolatile;
    if (redirectTo) {
      appVolatile.redirectTo = redirectTo;
    }

    if (this.nickname && authProviderToken) {
      this.loadLoginPage = true;
      this.login = this.nickname;
      this.password = authProviderToken;
      this.modifier = window.location.hostname;
      this.submit();
    } else if (stashToken) {
      this.login = '*STASH*';
      this.password = stashToken;
      this.submit();
    } else if (aliasType) {
      this.login = '*ALIAS*';
      this.password = aliasType + '|' + aliasName;
      this.submit();
    } else {
      if (this.authService.isLoggedIn()) {
        this.navigateOnLoggedIn();
      } else {
        this.loadLoginPage = true;
      }
    }

    const appConfig = (window as any).appConfig;
    if (appConfig?.general && appConfig.general.loginMessage) {
      this.customMessage = appConfig.general.loginMessage;
      this.customIcon = appConfig.general.loginIcon || 'campaign';
    }
    this.envName = appConfig?.environment || 'local';

    this.routingSub$ = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd && this.authService.isLoggedIn()) {
        this.navigateOnLoggedIn();
      }
    });
  }

  ngOnDestroy(): void {
    this.routingSub$?.unsubscribe();
  }

  async navigateOnLoggedIn(groupId?: string) {
    this.authService
      .getSession()
      .then(async (session) => {
        let displayName = session.user.email_address;
        if (session.user.first_name && session.user.last_name) {
          displayName = `${session.user.first_name} ${session.user.last_name}`;
        }
        this.fullStoryService.identify(session.user.id, {
          displayName,
          email: session.user.email_address,
          familyName: session.user.last_name,
          givenName: session.user.first_name,
          acccountId: session.user.account_id,
        });
        this.googleAnalyticsService.sendEvent('login');
        const appVolatile = (window as any).appVolatile;
        const [preferences, _] = await Promise.all([
          this.preferencesService.getPreferences(true),
          this.groupService.refreshAndSelectActiveGroup(groupId),
          this.semantriaAccount.sub.load(true), //change from DASH-4901
        ]);
        const userHomepage = preferences.homepage;
        const defaultUrl = this.computeDefaultUrl();
        let url = defaultUrl;
        if (appVolatile.redirectTo && appVolatile.redirectTo !== '/') {
          url = appVolatile.redirectTo;
          appVolatile.redirectTo = undefined;
        } else if (userHomepage) {
          url = userHomepage;
        }
        try {
          await this.router.navigateByUrl(url);
        } catch (error) {
          if (url === userHomepage) {
            this.alertService.showToast(`Homepage not found, redirecting to default start page`);
          }
          this.router.navigate([defaultUrl]);
        }
      })
      .catch((error) => {
        this.loadLoginPage = true;
        if (error.status === 401) {
          this.error = 'SessionExpired';
        }
      });
  }

  private computeDefaultUrl(): string {
    const groupId = this.groupService.getSelectedGroupId(true);
    return `/groups/${groupId}/projects`;
  }

  public submitSocial(serviceName: string) {
    this.isLoading = true;
    this.socialLoginService.login(serviceName);
  }

  public submit(): void {
    this.isLoading = true;
    this.error = '';

    this.authService.login(this.login, this.password, this.nickname, this.modifier).then(
      () => {
        this.navigateOnLoggedIn();
      },
      (err) => {
        this.isLoading = false;

        if (err.error) {
          this.error = err.error.type;
        } else {
          this.error = 'unknown';
        }
        if (this.login === '*ALIAS*') {
          this.login = '';
          this.password = '';
          this.modifier = '';
          this.loadLoginPage = true;
        }
      }
    );
  }

  onClickLearnMore() {
    window.open('https://www.lexalytics.com/spotlight/');
  }

  //Hack to override browser styling issue on autofill (DASH-4858)
  onRefreshInput() {
    if (this.initialLoad === true) {
      const login = this.login;
      const password = this.password;
      this.login = '';
      this.password = '';
      setTimeout(() => {
        this.login = login;
        this.password = password;
        this.initialLoad = false;
      });
    }
  }

  onValidateLoginName(loginForm) {
    loginForm.form.controls.login?.dirty && (this.login.length < 1 || this.login.trim() === '')
      ? (this.loginNameStatus = { state: EnumErrorStates.ERROR })
      : (this.loginNameStatus = undefined);
  }

  onValidatePassword(loginForm) {
    loginForm.form.controls.password?.dirty &&
    (this.password.length < 1 || this.password.trim() === '')
      ? (this.passwordStatus = { state: EnumErrorStates.ERROR })
      : (this.passwordStatus = undefined);
  }
}
